import { ArrowRight, CheckCircle2, Video, Clock, Calendar, Phone, Shield, Star, ChevronDown, Sparkles, Award, Users, TrendingUp, MessageSquare, Zap, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SofortBesichtigung() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    phone: '+41 ',
    preferredPlatform: 'whatsapp',
    selectedDate: '',
    selectedTime: '',
    message: '',
  });
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const airtableToken = import.meta.env.VITE_AIRTABLE_TOKEN;
      const airtableBaseId = import.meta.env.VITE_AIRTABLE_BASE_ID;

      const platformLabels: { [key: string]: string } = {
        'whatsapp': 'WhatsApp Video',
        'facetime': 'FaceTime',
        'googlemeet': 'Google Meet',
        'zoom': 'Zoom'
      };

      const response = await fetch(
        `https://api.airtable.com/v0/${airtableBaseId}/Anfragen%20Video%20Call%20Besichtigungen`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${airtableToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: {
              'Vorname': formData.vorname,
              'Nachname': formData.nachname,
              'Email': formData.email,
              'Telefon': formData.phone,
              'Bevorzugte Plattform': platformLabels[formData.preferredPlatform] || formData.preferredPlatform,
              'Wunschdatum': formData.selectedDate,
              'Wunschzeit': formData.selectedTime,
              'Nachricht': formData.message,
              'Status': 'Neu'
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error('Fehler beim Senden der Daten');
      }

      navigate('/danke');
    } catch (error) {
      console.error('Fehler:', error);
      alert('Es gab ein Problem beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const generateAvailableTimes = (date: string) => {
    const selectedDate = new Date(date);
    const today = new Date();
    const times: string[] = [];

    const isSameDay = selectedDate.toDateString() === today.toDateString();
    const currentHour = today.getHours();

    let startHour = isSameDay ? currentHour + 1 : 8;
    const endHour = 18;

    if (startHour > endHour) {
      return [];
    }

    for (let hour = startHour; hour <= endHour; hour++) {
      times.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < endHour) {
        times.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }

    return times;
  };

  const handleDateSelect = (date: string) => {
    setFormData({
      ...formData,
      selectedDate: date,
      selectedTime: ''
    });
    setAvailableTimes(generateAvailableTimes(date));
  };

  const handleTimeSelect = (time: string) => {
    setFormData({
      ...formData,
      selectedTime: time
    });
  };

  const getDateOptions = () => {
    const dates = [];
    const today = new Date();
    const startOffset = currentWeekOffset * 5;
    let daysAdded = 0;
    let dayOffset = startOffset;

    while (daysAdded < 5) {
      const date = new Date(today);
      date.setDate(today.getDate() + dayOffset);

      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        dates.push(date);
        daysAdded++;
      }
      dayOffset++;
    }
    return dates;
  };

  const handleNextWeek = () => {
    setCurrentWeekOffset(currentWeekOffset + 1);
  };

  const handlePreviousWeek = () => {
    if (currentWeekOffset > 0) {
      setCurrentWeekOffset(currentWeekOffset - 1);
    }
  };

  const formatDateLabel = (date: Date) => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Heute';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Morgen';
    } else {
      return days[date.getDay()];
    }
  };

  const formatDateDay = (date: Date) => {
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${date.getDate()}. ${months[date.getMonth()]}`;
  };

  const formatDateValue = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const renderAnswerWithBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const benefits = [
    {
      icon: Clock,
      title: 'Express-Service',
      description: 'Kostenlose Video-Besichtigung innerhalb von 1-2 Stunden - schneller geht es nicht!',
    },
    {
      icon: Zap,
      title: 'Sofort-Angebot',
      description: 'Erhalten Sie Ihr Festpreis-Angebot direkt nach dem Video-Call',
    },
    {
      icon: Phone,
      title: 'Bequem von zu Hause',
      description: 'Keine Anfahrtskosten und keine Wartezeiten - alles online',
    },
    {
      icon: Calendar,
      title: 'Flexible Termine',
      description: 'Auch am Wochenende und zu Ihren Wunschzeiten verfügbar',
    },
    {
      icon: Users,
      title: 'Persönliche Beratung',
      description: 'Umzugs-Experten mit langjähriger Erfahrung stehen Ihnen zur Seite',
    },
    {
      icon: Award,
      title: 'Präzise Kalkulation',
      description: 'Detaillierte Kostenschätzung in Echtzeit - transparent und fair',
    },
  ];

  const platforms = [
    { value: 'whatsapp', label: 'WhatsApp Video', icon: MessageCircle },
    { value: 'facetime', label: 'FaceTime', icon: Video },
    { value: 'googlemeet', label: 'Google Meet', icon: Video },
    { value: 'zoom', label: 'Zoom', icon: Video },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Formular ausfüllen',
      description: 'Teilen Sie uns Ihre Wunschzeit und bevorzugte Plattform mit.',
    },
    {
      step: '2',
      title: 'Bestätigung erhalten',
      description: 'Wir bestätigen Ihren Termin innerhalb von 15 Minuten.',
    },
    {
      step: '3',
      title: 'Video-Besichtigung',
      description: 'Zeigen Sie uns per Video Ihre Wohnung und das Umzugsgut.',
    },
    {
      step: '4',
      title: 'Sofort-Angebot',
      description: 'Sie erhalten direkt im Anschluss Ihr persönliches Festpreis-Angebot.',
    },
  ];

  const faqs = [
    {
      question: 'Wie schnell kann die Video-Besichtigung stattfinden?',
      answer: 'In der Regel können wir **innerhalb von 1-2 Stunden** einen Video-Call durchführen. An Wochenenden kann es etwas länger dauern, aber wir bemühen uns, auch hier schnellstmöglich einen Termin zu ermöglichen.',
    },
    {
      question: 'Welche Plattformen werden unterstützt?',
      answer: 'Wir bieten Video-Besichtigungen über **WhatsApp, FaceTime, Google Meet und Zoom** an. Sie können die für Sie bequemste Option wählen.',
    },
    {
      question: 'Was muss ich bei der Video-Besichtigung zeigen?',
      answer: 'Zeigen Sie uns einfach **alle Räume, die Möbel und Gegenstände**, die umgezogen werden sollen. Unsere Experten führen Sie durch den Prozess und stellen die richtigen Fragen, um ein **präzises Angebot** zu erstellen.',
    },
    {
      question: 'Wie lange dauert eine Video-Besichtigung?',
      answer: 'Eine typische Video-Besichtigung dauert **15-30 Minuten**, abhängig von der Größe Ihrer Wohnung und der Menge des Umzugsguts.',
    },
    {
      question: 'Bekomme ich wirklich sofort ein Angebot?',
      answer: '**Ja!** Direkt nach der Video-Besichtigung erstellen wir Ihr persönliches **Festpreis-Angebot** und senden es Ihnen per E-Mail zu. In vielen Fällen können wir Ihnen bereits **während des Calls** einen ersten Preis nennen.',
    },
    {
      question: 'Ist die Video-Besichtigung wirklich kostenlos?',
      answer: '**Ja, die Video-Besichtigung ist für Sie völlig kostenlos und unverbindlich.** Es entstehen **keine Anfahrtskosten** oder sonstige Gebühren.',
    },
    {
      question: 'Was passiert, wenn ich während der gewünschten Zeit nicht verfügbar bin?',
      answer: '**Kein Problem!** Wir bieten flexible Terminoptionen an. Wenn Ihre Wunschzeit nicht verfügbar ist, schlagen wir Ihnen **alternative Zeiten** vor.',
    },
    {
      question: 'Kann ich mehrere Räume oder Objekte zeigen?',
      answer: '**Natürlich!** Sie können uns **alle Räume und Gegenstände** zeigen, die für den Umzug relevant sind. **Je mehr Informationen** wir haben, **desto genauer** wird unser Angebot.',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Müller',
      location: 'Zürich',
      text: 'Die Video-Besichtigung war super praktisch! Innerhalb von 2 Stunden hatte ich einen Termin und 30 Minuten später mein Angebot. Kein lästiges Warten auf einen Vor-Ort-Termin.',
      rating: 5,
    },
    {
      name: 'Michael Weber',
      location: 'Basel',
      text: 'Ich war skeptisch, aber die Video-Besichtigung funktionierte perfekt. Der Berater war sehr professionell und das Angebot kam sofort. Absolute Empfehlung!',
      rating: 5,
    },
    {
      name: 'Julia Schmidt',
      location: 'Bern',
      text: 'Toll, dass man verschiedene Plattformen wählen kann. WhatsApp war für mich am einfachsten. Der ganze Prozess war unkompliziert und schnell.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-fade-in">
              <Video className="w-4 h-4 text-green-400" />
              <span className="text-sm font-medium">Kostenlose Video-Besichtigung</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight animate-fade-in-delay-1">
              Sofort-Besichtigung per Video-Call
            </h1>

            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay-2">
              Erhalten Sie Ihr persönliches Umzugsangebot innerhalb von 1-2 Stunden. Kostenlos, bequem und ohne Anfahrt - direkt per Video-Call!
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12 animate-fade-in-delay-2">
              <a
                href="#booking-form"
                className="group bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2 text-lg"
              >
                <span>Jetzt Video-Termin buchen</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="tel:0800700100"
                className="bg-slate-700 hover:bg-slate-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 border-2 border-slate-600"
              >
                0800 700 100 (Gratis)
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                  <platform.icon className="w-8 h-8 mx-auto mb-2 text-green-400" />
                  <div className="text-sm font-medium">{platform.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ihre Vorteile bei der Video-Besichtigung
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Schnell, bequem und kostenlos - so einfach war Umzugsplanung noch nie
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white rounded-xl shadow-md p-8 border-2 border-slate-100 hover:border-slate-900 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
                <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white w-14 h-14 rounded-xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform">
                  <benefit.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/Video-Besichtigung-Umzug-Profis.jpg"
                  alt="Video-Besichtigung Umzug Profis"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-2xl p-8 md:p-12 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '30px 30px'
                  }}></div>
                </div>
                <div className="relative z-10">
                  <div className="flex items-center space-x-2 mb-4">
                    <Sparkles className="w-6 h-6" />
                    <span className="text-sm font-semibold uppercase tracking-wider">Limitiertes Angebot</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">Jetzt buchen und 5% Frühbucher-Rabatt sichern!</h3>
                  <p className="text-lg text-green-50 leading-relaxed mb-6">
                    Buchen Sie Ihre Video-Besichtigung heute und erhalten Sie automatisch 5% Rabatt auf Ihren Umzug - nur für kurze Zeit!
                  </p>
                  <div className="bg-white text-green-600 rounded-full w-32 h-32 flex items-center justify-center shadow-2xl">
                    <div>
                      <div className="text-5xl font-bold">5%</div>
                      <div className="text-sm font-semibold">Rabatt</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              So einfach funktioniert's
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              In nur 4 Schritten zu Ihrem Festpreis-Angebot
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative flex items-center">
                <div className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 flex-1">
                  <div className="w-16 h-16 bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute -right-4 z-10">
                    <ArrowRight className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="booking-form" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-slate-100">
              <div className="text-center mb-8">
                <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full mb-4">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">Antwort in 1-2 Stunden</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Buchen Sie Ihre Video-Besichtigung
                </h2>
                <p className="text-lg text-slate-600">
                  Füllen Sie das Formular aus und wir melden uns umgehend bei Ihnen
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="vorname" className="block text-sm font-semibold text-slate-700 mb-2">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      id="vorname"
                      name="vorname"
                      value={formData.vorname}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition"
                      placeholder="Max"
                    />
                  </div>

                  <div>
                    <label htmlFor="nachname" className="block text-sm font-semibold text-slate-700 mb-2">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      id="nachname"
                      name="nachname"
                      value={formData.nachname}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition"
                      placeholder="Mustermann"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition"
                    placeholder="max@beispiel.ch"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    Telefon *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <span className="text-lg">🇨🇭</span>
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full pl-16 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition"
                      placeholder="+41 79 123 45 67"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="preferredPlatform" className="block text-sm font-semibold text-slate-700 mb-2">
                    Bevorzugte Video-Plattform *
                  </label>
                  <select
                    id="preferredPlatform"
                    name="preferredPlatform"
                    value={formData.preferredPlatform}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition"
                  >
                    {platforms.map((platform) => (
                      <option key={platform.value} value={platform.value}>
                        {platform.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-slate-700">
                      Wunschdatum *
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={handlePreviousWeek}
                        disabled={currentWeekOffset === 0}
                        className="p-2 rounded-lg border-2 border-slate-200 hover:border-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronLeft className="w-5 h-5 text-slate-700" />
                      </button>
                      <span className="text-sm text-slate-600 font-medium">
                        {currentWeekOffset === 0 ? 'Diese Woche' : 'Nächste Woche'}
                      </span>
                      <button
                        type="button"
                        onClick={handleNextWeek}
                        disabled={currentWeekOffset === 1}
                        className="p-2 rounded-lg border-2 border-slate-200 hover:border-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronRight className="w-5 h-5 text-slate-700" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
                    {getDateOptions().map((date) => {
                      const dateValue = formatDateValue(date);
                      const isSelected = formData.selectedDate === dateValue;
                      return (
                        <button
                          key={dateValue}
                          type="button"
                          onClick={() => handleDateSelect(dateValue)}
                          className={`p-4 rounded-lg border-2 transition-all duration-200 text-center ${
                            isSelected
                              ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                              : 'border-slate-200 bg-white hover:border-slate-400 hover:shadow-md'
                          }`}
                        >
                          <div className={`text-sm font-semibold mb-1 ${
                            isSelected ? 'text-white' : 'text-slate-900'
                          }`}>
                            {formatDateLabel(date)}
                          </div>
                          <div className={`text-xs ${
                            isSelected ? 'text-slate-200' : 'text-slate-600'
                          }`}>
                            {formatDateDay(date)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {formData.selectedDate && availableTimes.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Verfügbare Zeiten *
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                      {availableTimes.map((time) => {
                        const isSelected = formData.selectedTime === time;
                        return (
                          <button
                            key={time}
                            type="button"
                            onClick={() => handleTimeSelect(time)}
                            className={`py-3 px-4 rounded-lg border-2 transition-all duration-200 font-semibold ${
                              isSelected
                                ? 'border-green-600 bg-green-600 text-white shadow-lg'
                                : 'border-slate-200 bg-white hover:border-green-400 hover:shadow-md text-slate-700'
                            }`}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {formData.selectedDate && availableTimes.length === 0 && (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 text-center">
                    <p className="text-amber-800 font-medium">
                      Keine verfügbaren Zeiten für heute mehr. Bitte wählen Sie einen anderen Tag.
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                    Zusätzliche Informationen (optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition resize-none"
                    placeholder="Besondere Anforderungen, Anzahl der Zimmer, etc. Falls Sie nicht alle Informationen angeben, werden wir diese im Videogespräch erfragen."
                  ></textarea>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 border-2 border-slate-200">
                  <div className="flex items-start space-x-2">
                    <Shield className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Ihre Daten werden vertraulich behandelt und nur für die Terminvereinbarung verwendet.
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.selectedDate || !formData.selectedTime}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <span>Wird gesendet...</span>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5" />
                      <span>Video-Termin jetzt buchen</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Unser Experten-Team für Ihre Video-Besichtigung
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Erfahrene Umzugsprofis beraten Sie persönlich und kompetent
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-20">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-2xl">
                <div className="relative h-80 overflow-hidden bg-white">
                  <img
                    src="/DSC_0187.jpg"
                    alt="Herr G. Kolarov - Geschäftsführer"
                    className="w-full h-full object-contain object-[center_45%] transition-transform duration-500 hover:scale-150 scale-110"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Herr G. Kolarov</h3>
                  <p className="text-slate-600 font-medium mb-3">Geschäftsführer</p>
                  <p className="text-sm text-slate-500 italic">Über 10 Jahre Erfahrung in Umzüge ins Ausland</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-2xl">
                <div className="relative h-80 overflow-hidden bg-white">
                  <img
                    src="/DSC_0168 copy.jpg"
                    alt="Frau D. Kolarov - Geschäftsführerin"
                    className="w-full h-full object-contain object-[center_45%] transition-transform duration-500 hover:scale-150 scale-110"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Frau D. Kolarov</h3>
                  <p className="text-slate-600 font-medium mb-3">Geschäftsführerin</p>
                  <p className="text-sm text-slate-500 italic">Über 10 Jahre Erfahrung in internationalen Umzügen und Kundenbetreuung</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-2xl">
                <div className="relative h-80 overflow-hidden bg-white">
                  <img
                    src="/Herr-Rothenbuehler-Umzugsberater copy.jpg"
                    alt="Herr R. Rothenbühler - Umzugsberater"
                    className="w-full h-full object-contain object-center transition-transform duration-500 hover:scale-150 scale-100"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Herr R. Rothenbühler</h3>
                  <p className="text-slate-600 font-medium mb-3">Umzugsberater</p>
                  <p className="text-sm text-slate-500 italic">Über 5 Jahre Erfahrung in Auslandsumzügen, Verzollungen und Logistik</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg border-2 border-slate-100">
                <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <TrendingUp className="w-8 h-8" />
                </div>
                <div className="text-4xl font-bold text-slate-900 mb-2">5000+</div>
                <div className="text-slate-600 font-medium">Erfolgreiche Umzüge</div>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg border-2 border-slate-100">
                <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Star className="w-8 h-8" />
                </div>
                <div className="text-4xl font-bold text-slate-900 mb-2">4.9/5</div>
                <div className="text-slate-600 font-medium">Kundenbewertung</div>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-lg border-2 border-slate-100">
                <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <MessageSquare className="w-8 h-8" />
                </div>
                <div className="text-4xl font-bold text-slate-900 mb-2">98%</div>
                <div className="text-slate-600 font-medium">Weiterempfehlung</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Das sagen unsere Kunden
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Überzeugen Sie sich von der Qualität unserer Video-Besichtigungen
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                <p className="text-slate-700 leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>

                <div className="border-t-2 border-slate-200 pt-4">
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Häufig gestellte Fragen
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Alles, was Sie über unsere Video-Besichtigungen wissen müssen
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-bold text-slate-900 pr-8">{faq.question}</span>
                  <ChevronDown
                    className={`w-6 h-6 text-slate-600 flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-600 leading-relaxed">{renderAnswerWithBold(faq.answer)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit für Ihre kostenlose Video-Besichtigung?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Buchen Sie jetzt Ihren Termin und erhalten Sie Ihr Festpreis-Angebot innerhalb von 1-2 Stunden!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="#booking-form"
              className="group bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2 text-lg"
            >
              <span>Jetzt buchen</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="tel:0800700100"
              className="bg-slate-700 hover:bg-slate-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 border-2 border-slate-600 flex items-center space-x-2"
            >
              <Phone className="w-5 h-5" />
              <span>0800 700 100 anrufen</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
